// The bar across the top: the legacy Nav.
//
// The customer's logo on the left (the product mark when there is none), the
// menu icon in the customer's colour on the right. On a desktop the icon opens
// a drawer from the right with the settings, the dark-mode switch and signing
// out; on a phone it goes to the settings page, and the bar carries the
// illustration fading into the page.

import { Fragment, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  ClickAwayListener,
  Drawer,
  Grid,
  IconButton,
  Switch,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { api } from "~/api";
import { useBootstrap } from "~/bootstrap";
import { useT } from "~/i18n";
import { setDark, useThemeState } from "~/theme";
import { skipIntro, useIntro } from "~/intro";
import LogoIcon from "~/legacy/icons/LogoIcon";
import MenuIcon from "~/legacy/icons/MenuIcon";
import CustomSettingsIcon from "~/legacy/icons/SettingsIcon";
import { UserSettings } from "~/features/usersettings";

// The legacy test for Safari, which placed the drawer under the bar instead of
// behind it.
const isSafari =
  typeof window !== "undefined" &&
  (/constructor/i.test(String((window as unknown as { HTMLElement: unknown }).HTMLElement)) ||
    (function (p: unknown) {
      return String(p) === "[object SafariRemoteNotification]";
    })(
      !(window as unknown as { safari?: { pushNotification?: unknown } }).safari ||
        (window as unknown as { safari?: { pushNotification?: unknown } }).safari?.pushNotification,
    ));

/** The mobile bar's illustration, recoloured in the customer's colour — the legacy MbmTopMobileCustom. */
function mobileTop(primary: string): string {
  const svg = MOBILE_TOP.split("__PRIMARY__").join(primary);
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export async function signOut() {
  const out = await api<{ endSessionUrl?: string }>("/api/portal/session", { method: "DELETE" }).catch(() => undefined);
  // A customer on an identity provider is signed out there as well, which
  // the legacy application only did for Microsoft and only by hand.
  window.location.href = out?.endSessionUrl ?? "/login";
}

export function Layout() {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const b = useBootstrap();
  const { dark } = useThemeState();
  const location = useLocation();
  const navigate = useNavigate();
  const intro = useIntro();

  const [error, setError] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [settings, setSettings] = useState(false);
  const [errorHover, setErrorHover] = useState(false);

  // The splash only ever ran on the start page.
  useEffect(() => {
    if (location.pathname !== "/") skipIntro();
  }, [location.pathname]);

  const hidden = intro === "running";
  const logo = b.company.branding.logoUrl;

  return (
    <Fragment>
      <ImpersonationBanner />
      <UserSettings open={settings} onClose={() => setSettings(false)} />
      <Drawer
        anchor="right"
        open={drawer}
        hideBackdrop
        sx={{
          "& .MuiDrawer-paper": {
            backgroundImage: "none",
            mt: isSafari ? 11.25 : 0,
            boxShadow: isSafari ? "none" : "328px 0px 8px 0px rgba(0, 0, 0, 0.20), 0px 14px 28.8px 0px rgba(0, 0, 0, 0.24)",
            "&::-webkit-scrollbar": { width: 0 },
          },
        }}
      >
        <Box sx={{ height: isSafari ? 0 : 99 }} />
        <ClickAwayListener onClickAway={() => setDrawer(false)}>
          <Box sx={{ width: "328px", height: "100%" }}>
            <Box
              sx={{
                width: "100%",
                pl: 2,
                pb: 2,
                mt: 6.5,
                borderBottom: 1,
                borderBottomColor: theme.palette.mode === "dark" ? "divider" : "info.main",
              }}
            >
              <Typography noWrap sx={{ fontSize: "17px", fontWeight: 600, lineHeight: "22px", letterSpacing: "-0.43px" }}>
                Mobilbox Manager
              </Typography>
            </Box>
            <Grid container sx={{ px: 1, mt: 2 }}>
              <IconButton
                onClick={() => {
                  setSettings(true);
                  setDrawer(false);
                }}
                sx={{ width: "100%", borderRadius: 6, justifyContent: "flex-start" }}
              >
                <CustomSettingsIcon />
                <Typography sx={{ ml: 2, fontSize: "17px", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>
                  {t("settings")}
                </Typography>
              </IconButton>
            </Grid>
            <Grid container sx={{ px: 1 }}>
              <IconButton onClick={() => setDark(!dark)} sx={{ width: "100%", borderRadius: 6, justifyContent: "flex-start", pl: 0 }}>
                <Switch checked={dark} color="primary" size="small" />
                <Typography sx={{ ml: 0.5, fontSize: "17px", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>
                  {t("dark-mode")}
                </Typography>
              </IconButton>
            </Grid>
            <Grid container sx={{ px: 1 }}>
              <IconButton
                color={errorHover ? "error" : "default"}
                onMouseEnter={() => setErrorHover(true)}
                onMouseLeave={() => setErrorHover(false)}
                onClick={() => void signOut()}
                sx={{ width: "100%", borderRadius: 6, justifyContent: "flex-start" }}
              >
                <LogoutIcon sx={{ color: "inherit", fontSize: 20, mt: 0.2 }} />
                <Typography sx={{ ml: 2, fontSize: "17px", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" }}>
                  {t("logout")}
                </Typography>
              </IconButton>
            </Grid>
          </Box>
        </ClickAwayListener>
      </Drawer>

      <AppBar
        position="relative"
        color="transparent"
        // display "inline" is the legacy value, and it is what hides the
        // AppBar's shadow: an inline box around a block draws none.
        sx={{ display: "inline", zIndex: theme.zIndex.drawer + 1, visibility: hidden ? "hidden" : "visible" }}
      >
        <Toolbar variant="dense" sx={{ bgcolor: "background.default", pl: 0 }}>
          <Box
            onClick={() => navigate("/")}
            sx={{
              height: belowsm ? 86 : 90,
              width: belowsm ? 50 : 100,
              background: "transparent",
              zIndex: theme.zIndex.drawer + 1,
              mr: 1,
            }}
          >
            {logo && !error ? (
              <Box sx={{ height: 48, display: "flex", alignItems: "center", ml: belowsm ? 3 : 1, mt: belowsm ? 0 : 2.5 }}>
                <img alt="logo" src={logo} onError={() => setError(true)} height="100%" />
              </Box>
            ) : (
              <Box sx={{ mt: belowsm ? 0 : 1 }}>
                <LogoIcon fontSize={belowsm ? 40 : 48} />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              zIndex: theme.zIndex.appBar,
              backgroundImage: belowsm
                ? theme.palette.mode === "light"
                  ? `linear-gradient(to bottom, rgba(255, 255, 255, 0.5), white 90%), url("${mobileTop(theme.palette.primary.main)}")`
                  : `linear-gradient(to bottom, rgba(18, 18, 18, 0.5), #121212 90%), url(/images/mbm-top-mobile-dark.svg)`
                : "none",
              backgroundSize: "cover",
              height: belowsm ? 120 : 48,
              mt: 2,
            }}
          />
          <IconButton
            onClick={belowsm ? () => navigate("/einstellungen") : () => setDrawer(true)}
            sx={{ mb: belowsm ? 10 : 0, width: 40 }}
          >
            <MenuIcon color={theme.palette.primary.main} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Outlet />
    </Fragment>
  );
}

/**
 * An administrator is using the portal in somebody else's name.
 *
 * New against the legacy application, so it is drawn in the old palette's
 * warning colour rather than in something of its own. Deliberately not in the
 * customer's colour, which can be grey, amber or red: a warning that looks like
 * the rest of the interface is not a warning. It cannot be dismissed, because
 * the thing it warns about cannot be: every save on every screen below it is
 * written in this person's name.
 */
function ImpersonationBanner() {
  const t = useT();
  const b = useBootstrap();
  const by = b.me.impersonatedBy;
  const [busy, setBusy] = useState(false);

  // So that the tab strip says it too, for a window left in the background.
  useEffect(() => {
    if (!by) return;
    const before = document.title;
    document.title = `[${t("impersonation-title")}] ${before}`;
    return () => {
      document.title = before;
    };
  }, [by, t]);

  if (!by) return null;

  return (
    <Box
      role="status"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1400,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 0.5,
        px: 2,
        py: 1,
        bgcolor: "#FCD34D",
        borderBottom: "2px solid #D97706",
        color: "#000",
        fontSize: 14,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t("impersonation-title")}</Typography>
      <Typography sx={{ fontSize: 14 }}>
        {b.me.fullName} · {t("impersonation-by")} {by.name || by.email} ({by.email})
      </Typography>
      <Typography sx={{ fontSize: 14, opacity: 0.75, display: { xs: "none", lg: "inline" } }}>{t("impersonation-note")}</Typography>
      <Button
        disabled={busy}
        variant="outlined"
        size="small"
        onClick={() => {
          setBusy(true);
          void api("/api/portal/session", { method: "DELETE" })
            .catch(() => {})
            .finally(() => {
              // The tab was opened by the administration interface, so it can
              // usually close itself. Where the browser refuses, "/" is the
              // right landing and not "/login": the administrator's own portal
              // session was never touched.
              window.close();
              window.location.replace("/");
            });
        }}
        sx={{ ml: "auto", color: "#000", borderColor: "rgba(0,0,0,0.7)", borderWidth: 2, fontWeight: 600, textTransform: "none", "&:hover": { borderWidth: 2, borderColor: "#000" } }}
      >
        {t("impersonation-end")}
      </Button>
    </Box>
  );
}

// The legacy MbmTopMobileCustom, with the customer's colour where the data
// URI interpolated theme.palette.primary.main.
const MOBILE_TOP = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488.68 209.96"><defs><style>.cls-1{fill:none;stroke:#1d1d1b;stroke-miterlimit:10;stroke-width:2px;}.cls-2,.cls-3{opacity:.3;}.cls-4,.cls-3{fill:__PRIMARY__;fill-rule:evenodd;stroke-width:0px;}</style></defs><g id="Ebene_2"><g id="Mann" class="cls-2"><path class="cls-4" d="M314.29,32.47s-1.13-2.27-2.5-4.16c-1.38-1.89-2.5-2.91-2.5-2.91,0,0,7.36-11.54,18.36-16.68,10.38-4.86,19.63-5.35,21.69-3.37,2.06,1.98.83,3.33.83,3.33,0,0,18.69-.59,20.86,2.05s7.34,22.09,6.26,35.38c-1.08,13.29-7.51,27.07-7.51,27.07l-5.84-12.48s3.91-8.95,3.75-11.25c-.16-2.3.93-8.75-1.25-9.57-2.18-.83-2.71-1.51-4.17.42-1.46,1.93-5.84,8.75-5.84,8.75,0,0-1.97-5.49-3.34-9.99-1.37-4.5-2.09-14.15-2.09-14.15,0,0-7.23,1.63-10.01,2.52-2.78.89-5.84,1.26-5.84,1.26l.42-4.58s-5.95,1.13-10.01,3.35c-4.06,2.22-11.26,5.02-11.26,5.02Z"/><path class="cls-4" d="M292.81,120.74s4.17.95,9.71,1.67c5.21.67,11.61,1.02,11.83.83.21-.18-4.29-26.94-6.34-27.48-4.21-1.1-20.99-.01-22.38.42-1.4.43,7.18,24.56,7.18,24.56Z"/><path class="cls-4" d="M286.9,166.12s-5.3,8.96-8.87,12.91c-3.57,3.95-14.36,12.91-14.36,12.91,0,0,9.33,5.38,17.32,7.91,7.99,2.53,24.85,1.16,25.76.42.92-.74-2.42-14.25-7.18-20.4-4.76-6.15-12.67-13.74-12.67-13.74Z"/><path class="cls-4" d="M374.74,94.1s-3.89,17.29-10.14,22.48c-6.25,5.19-15.56,14.92-23.23,7.08-7.67-7.84-8.02-22.9-8.02-22.9l-17.32,7.08,4.22,14.57,5.91,3.33s5.39,12.04,5.49,14.57c.1,2.53,1.27,12.91,1.27,12.91,0,0,6.29,8.44,1.69,18.32-4.6,9.88-11.83,15.82-11.83,15.82,0,0,26.44,9.19,38.86,12.49,12.41,3.3,33.47,4.16,35.48,3.75,2.01-.41,13.55-34.26,16.05-45.38,2.5-11.11-.8-46.78-2.11-51.21-1.31-4.42-36.32-12.91-36.32-12.91Z"/></g><g id="Frau_2" class="cls-2"><path class="cls-4" d="M100.25,115.36l-27.18,17.15s-1.37,24.64,1.25,31.37c2.63,6.73,7.62,22.54,21.33,34.71-6.42,6.35-16.73,9.2-16.73,9.2l-8.78-16.73s19.38-33.66,25.51-39.31c6.13-5.66,15.89-16.73,15.89-16.73,0,0-9.1-14.15-11.29-19.66Z"/><path class="cls-4" d="M96.9,199.42s11.48,4.78,29.28,5.44c17.8.66,34.29-2.93,34.29-2.93,0,0,10-27.64,10.04-37.22.04-9.58-13.38-36.38-13.38-36.38,0,0-4.81,7.68-7.11,8.36-2.3.69-8.78,0-8.78,0,0,0,4.71,25.81,4.18,27.6-1.33,4.53-5.6,3.68-8.36,1.67-2.76-2.01-13.81-14.6-14.64-15.89-.82-1.3-3.83,7.94-5.85,14.22-2.03,6.28-10.47,29.86-19.66,35.13Z"/><path class="cls-4" d="M191,178.93s-19.87,13.79-15.06,25.51c1.47,3.57,22.58-.42,22.58-.42,0,0-4.35-6.75-5.85-11.71-1.51-4.96-1.67-13.38-1.67-13.38Z"/></g><path id="Frau_1" class="cls-3" d="M216.45,98.15c-1.11,5.59-6.62,19.15-6.62,19.15,0,0-3.15-3.82-7.45-6.18-4.56-2.51-9.88-3.57-10.35-3.3-.91.52-.41,6.6-.41,6.6,0,0-3.2,2.78-4.14,4.12-.94,1.34,2.9,10.31,2.9,10.31l-.41,3.3c1.25,2.37,2.65,4.83,4.2,7.35,2.05,3.33,4.12,6.38,6.15,9.14,0,0,.24,1.82-.83,2.47-.88.54-16.09-2.18-21.87-2.82-1.77-.19-2.12-1.96-1.64-3.8,2.56-9.83,12.79-41.65,15.13-50.3.52-1.92,1.2-2.77,3.41-2.44,5.05.75,15.84,2.49,20.68,3.49,1.21.25,1.76.31,1.25,2.92ZM156.86,94.22s4.81,3.19,2.07,10.72c-1.13,3.11-4.78,3.87-4.93,3.91-4.43,1.21-7.15-2.53-11.6-.94-1.89.67-2.54,1.75-3.36,1.35-1.09-.54-.6-2.74-.39-8.43.21-5.7-.29-8.46,1.24-8.66,3.9-.5,3.99-.25,5.79-1.65,1.8-1.4,10.76-19.79,10.76-19.79v-13.6s4.14-7.42,4.14-7.42c0,0,3.3,4.84,6.34,10.91,3.7,7.38,5.92,12.73,4.83,18.36-1.87,9.64-12.93,14.43-14.9,15.25ZM122.51,29.5c-.37,3.9-9.93,15.36-13.66,16.08-1.76.34-3.77-2-5.38-.82-1.8,1.31-3.14,6.24-3.73,8.66-1.11,4.58,3.68,16.8,6.62,25.15,2.94,8.35-2.89,32-4.14,31.74-1.35-.28-18.21-1-26.33-14.27-10.56-17.25,1.88-29.98,3.56-32.31,1.69-2.33-.81-8.97.83-18.96,1.64-10,13.37-10.3,15.73-12.37,2.36-2.06,4.5-15.13,18.21-22.26,13.71-7.13,42.48,8.82,41.8,17.73s-33.52,1.65-33.52,1.65Z"/></g><g id="Ebene_1"><path class="cls-1" d="M25.02,208.41s35.43.81,47.74-20.58c9.92-17.23,14.44-24.5,23.78-36.81,7.91-10.43,23.6-23.05,30.72-29.54,7.12-6.49,13.07-12.69,14.88-13.21,1.26-.36,12.96,1.2,12.96,1.2l9.6,2.88s11.24,5.98,11.28,6.48c.08,1-.7,3.23-3.6,2.88-1.67-.2-6.52-1.05-9.51-2.64-2.53-.67-4.65-1.21-4.65-1.21,0,0,2.42,5.61,1.2,7.68-1.22,2.08-7.08,9.35-9.6,11.05s-14.87-.06-16.56-.96c-1.69-.9-11.22,13.24-13.68,20.89-2.46,7.65-9.98,30.61-17.76,38.42-7.78,7.81-21.53,14.04-23.28,13.21s-13.07-23.59-12.48-39.62c.59-16.03,1.43-35.82,1.68-36.74.25-.92.86-1.8,4.32-3.36,3.46-1.57,29.26-16.46,31.2-17.77,1.94-1.31,6.4-20.08,6.96-26.18.56-6.1-2.16-18.97-2.16-18.97,0,0-7.23-3.91-7.2-9.61.03-5.7,2.01-11.16,3.36-10.57,1.82.81,5.54,3.46,6.24,4.08,1.51,1.34,3.69-2.39,6.96-6,3.27-3.62,6.96-11.05,6.96-11.05,0,0,15.66,3.78,22.08,4.08,6.42.3,18.82-5.09,18.96-6.96.14-1.87-14.8-29.12-44.64-25.21-22.11,2.89-26.05,25.64-27.84,25.93-.61.1-11.54,7.01-11.52,16.09.02,9.08,1.43,16.77,1.44,17.53,0,.76-8.75,11.05-8.64,18.25.05,3.54,3.38,15.32,10.56,21.13,7.43,6.02,19.71,7.9,19.2,7.2,1.86,2.55,7.35,16.72,15.84,29.04,9.82,14.25,22.76,26.66,24.24,25.95,2.77-1.32-4.68-38.97-8.64-51.87-.6-11.53,1.33-15.64.96-16.32-.37-.68-5.86-1.37-8.64-2.89-2.78-1.53-8.91-7.31-8.64-7.92.69.23,6.85,4.82,14.64,5.52,7.79.71,11.4-.06,12.24-.48.84-.42,10.63-17.86,11.04-20.89.41-3.04.17-13.09.24-13.45.07-.36,3.59-6.43,3.84-7.68.25-1.26.74-9.18.96-9.61.22-.43,7.97,16.55,10.08,21.37,2.11,4.82,6,16.87,1.92,24.49-4.08,7.62-11.05,10.02-11.04,10.33.01.31,2.23,5.78.96,8.64-3.16,7.13-15.17,9.75-15.84,10.09-.67.33,5.42,5.39,8.4,7.92,2.98,2.54,19.56,10.34,20.64,11.05,1.08.7,9.51,26.2,11.28,34.34,1.77,8.14,2.86,16.76,7.44,25.41,2.06,3.89,8.08,8.78,10.8,10.08-.94,3.96-24.04,8.52-30.24,2.45-2.53-2.47-.5-10.31,3.84-16.81,3.32-4.97,8.25-8.72,12.48-11.77,6.9-4.97,14.2-22.36,14.4-23.05.2-.69-7.79-5.41-11.28-11.05-3.49-5.64-5.14-9.24-5.28-10.33-.14-1.09.72-3.12.72-3.12,0,0-3.54-8.93-3.12-10.57.42-1.63,4.14-3.83,4.08-4.32-.06-.49-.55-6.09.24-6.72.79-.64,9.67,2.68,12.72,5.04,3.05,2.36,7.92,6.96,7.92,6.96,0,0-7.36,23.51-9.84,30.02-.64,1.67-1.83,2.42-3.6,2.16-5.8-.85-19.85-2.46-25.2-3.6-1.8-.38-2.37-1.63-1.92-3.12,2.79-9.29,14.97-47.56,17.52-55.71.6-1.91,2.05-2.62,3.36-2.4,5.61.94,21.41,3.26,26.64,4.08,1.04.16,2.06,1.19,1.44,3.12-1.6,4.95-6.48,19.69-6.48,19.69,0,0,1.16,4.09,2.88,7.2,1.86,3.35,4.44,10.34,5.04,13.45.6,3.11-.43,14.11-.72,16.31-.29,2.2-.86,17.1-3.6,25.68-2.74,8.58-7.03,20.04-9.6,23.28-1.41,1.78,19.71,8.17,32.64,8.21l16.77-.11c33.69-.56,47.94-7.7,48.11-8.29.17-.59-2.5-15.53-11.04-24.97-8.54-9.45-15.83-17.79-16.08-18.25-.57-1.05,3.1-2.77,1.68-15.61-.74-6.72-1.17-12.71-2.16-13.69-.99-.98-8.24-4.93-8.16-5.76.08-.84,2.59.14,6,.24,3.41.1,5.81-1.04,6.24-1.44.43-.4,0-4.08,0-4.08,0,0-8.07-2.75-11.52-3.12-3.45-.37-10.79-.75-13.44-.48-2.65.27-5.15.05-6.24,2.64-1.09,2.59-.69,3.77-.72,4.32-.01.25-1.29,3.16-1.92,6.48-.76,4-.84,8.29-.72,9.37.12,1.04,3.93,10.64,7.2,14.89,3.27,4.25,7.86,9.91,8.16,10.81.3.89-1.22,28.85,0,30.74,1.22,1.89,20.11-17.46,28.32-35.54,8.21-18.08,9.92-41.44,10.56-44.42.64-2.98,2.94-5.89,7.2-7.2,7.47-2.31,22.8-7.68,22.8-7.68.23,1.28.03,23.76,10.98,24.73,16.34,1.45,22.76-22.39,24.54-30.99.17-.82-5,5.62-11.04,9.85-5.69,3.98-12.07,6.2-12.72,6.48-1.35.59-2.98-3.57-4.8-6.72-1.82-3.15-4.56-6-4.56-6,0,0-7.84.38-9.12-.48-1.28-.86-4.6-10.52-4.32-11.05.28-.53,6.29,5.76,8.88,7.2,3.52,1.96,32.05-8.95,38.16-16.57,5.85-7.3,8.06-24.09,7.68-36.26-.38-12.17-2.91-22.84-6-25.45-3.09-2.62-11.76-3.36-11.76-3.36,0,0-8.28-10.55-24.24-7.2s-30.44,21.5-30.48,22.09c-.04.55,4.39,1.32,6.17,3.67,1.67,2.2,4.15,5.7,4.15,5.7,0,0,5.12-2.65,10.32-4.8,4.73-1.96,9.56-3.44,10.08-3.36,1.09.17-1.71,5.11,0,4.56,2.64-.85,15.1-4.42,15.6-4.08.5.33.7,7.13,1.44,11.77.68,4.24,3.25,12.55,3.84,12.97.59.42,4.75-7.54,5.76-8.64,1.01-1.11,2.73-1.71,3.84-1.2,1.11.51,1.83,7.08,1.68,9.37-.15,2.28-3.2,10.06-3.6,10.57-.4.51,3.79,10.2,6.24,15.85,2.28,5.26,3.35,6.74,4.22,7.78.67.81,20.14,9.58,26.74,12.39,13.39,5.7,14.76,5.18,15.84,10.09,1.79,8.12,4.09,27.18,2.16,43.46-2.47,20.78-18.22,57.33-18.96,58.35-.74,1.02-22.25,1.05-42-4.32-19.75-5.37-40-12.44-40.32-12.73-.16-.14-.48-5.17.24-10.09.75-5.13,2.53-10.15,2.4-10.33-.25-.33-5.19-2.44-10.08-5.28-4.97-2.89-9.63-6.46-10.56-7.44-1.85-1.95-4.89-13.07-5.04-14.17-.15-1.1.13-9.41.24-9.61.11-.2-3.14-4.54-4.08-5.76-.94-1.22,1.59-4.51,2.16-6,.47-1.23-6.06-20-8.16-26.9-.55-1.79-.02-2.9,1.92-3.12,4.93-.55,15.75-1.26,21.12-1.68,2.28-.18,2.94.74,3.36,2.16,2.4,8.11,9.36,30.74,9.36,30.74,0,0,5.21,2.09,5.76,2.88.55.79,3.94,10.04,4.8,15.13.86,5.08,1.2,11.77,1.2,11.77,0,0,5.41,3.53,13.2,6.96,7.79,3.43,27.32,7.19,27.6,7.44.28.26,4.63,1.21,6.72,4.08,2.09,2.87,4.14,6.98,4.56,6.72.42-.25.98-6.21-2.16-9.85-3.14-3.63-5.44-7.05-5.76-7.68-.32-.64,10.7-25.19,11.76-26.9,1.06-1.7.49,1.51-.48,5.76-.97,4.25-4.53,18.16-4.32,19.21.21,1.06,6.95,5.72,12.72,13.93,5.77,8.2,14.44,22.99,29.52,29.54,15.08,6.55,30.48,6.24,30.48,6.24"/></g></svg>`;
